import { useId, useState } from 'react';
import type { EligibilityFieldProvenance } from '@features/patient-coverages/types';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Button, Collapse, IconButton, Popover, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

interface Props {
  fieldLabel: string;
  source: EligibilityFieldProvenance;
  actorDisplayName?: string;
  compact?: boolean;
}

const SOURCE_LABELS: Record<EligibilityFieldProvenance['kind'], string> = {
  automatic: 'Automatic check',
  manual: 'Manual record',
  legacy_initialize: 'Earlier history',
  accept_copay: 'Accepted payer evidence',
  keep_current: 'Retained current benefit',
};

function contactTime(value: string | null | undefined): string {
  if (!value) return 'Original contact time unavailable';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Original contact time unavailable';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
}

/** Authorized source metadata, never used to fill an authoritative null benefit. */
export function CoverageSourceDetails({
  fieldLabel,
  source,
  actorDisplayName,
  compact = false,
}: Props) {
  const { eligibilityRecord: tracking } = useEventTracking();
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => {
    tracking.sourceClosed();
    setExpanded(false);
    setAnchor(null);
  };
  const sourceLabel =
    source.legacy_basis === 'unresolved'
      ? 'Earlier history · source uncertain'
      : SOURCE_LABELS[source.kind];
  const metadata = (
    <Stack spacing={0.5} sx={{ p: 1.5, borderLeft: 2, borderColor: 'divider' }}>
      <Typography sx={{ fontWeight: 600 }}>{sourceLabel}</Typography>
      <Typography variant="body2">{contactTime(source.observed_at)}</Typography>
      {source.method && (
        <Typography variant="body2">
          Method: {source.method === 'portal' ? 'Payer portal' : 'Phone'}
        </Typography>
      )}
      {(actorDisplayName || source.actor) && (
        <Typography variant="body2">
          Recorded by:{' '}
          {actorDisplayName ??
            (source.actor?.kind === 'system'
              ? 'Automated system'
              : source.actor?.kind === 'agent'
                ? 'Agent'
                : source.actor?.kind === 'patient'
                  ? 'Account holder'
                  : 'Team member')}
        </Typography>
      )}

      {source.representative_name && (
        <Typography variant="body2">Representative: {source.representative_name}</Typography>
      )}
      {source.reference_number && (
        <Typography variant="body2">Reference: {source.reference_number}</Typography>
      )}
      {source.notes && (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          Historical note: {source.notes}
        </Typography>
      )}
    </Stack>
  );
  const toggle = (event: React.MouseEvent<HTMLElement>) => {
    if (expanded) close();
    else {
      tracking.sourceOpened();
      setExpanded(true);
      setAnchor(event.currentTarget);
    }
  };
  return (
    <Box>
      {compact ? (
        <IconButton
          size="small"
          aria-label={`${expanded ? 'Hide' : 'View'} ${fieldLabel.toLowerCase()} source`}
          aria-expanded={expanded}
          aria-controls={expanded ? id : undefined}
          onClick={toggle}
          sx={{ p: 0.25 }}
        >
          <InfoOutlined sx={{ fontSize: 15 }} />
        </IconButton>
      ) : (
        <Button
          size="small"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={toggle}
          sx={{ textAlign: 'left' }}
        >
          {expanded ? 'Hide' : 'View'} {fieldLabel.toLowerCase()} source
        </Button>
      )}
      {compact ? (
        <Popover
          id={id}
          open={expanded}
          anchorEl={anchor}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ paper: { sx: { maxWidth: 360 } } }}
        >
          {metadata}
        </Popover>
      ) : (
        <Collapse in={expanded} id={id}>
          {metadata}
        </Collapse>
      )}
    </Box>
  );
}
